/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from "react";
import { FiArrowUp, FiPlus } from "react-icons/fi";
import { LINE_STATUS } from "../constants";
import MedicineLine from "./MedicineLine";
import MedicinePicker from "./MedicinePicker";

/**
 * Step three: the pharmacist's actual work.
 *
 * The count that matters is how many lines still need a decision, so the jump
 * bar leads with that and lets the pharmacist move through them without
 * scrolling by hand. Lines that are matched are quiet; unresolved lines
 * announce themselves.
 */
const MedicinesStep = ({
    lines,
    activeLineId,
    onSelectLine,
    onChangeLine,
    onResolveLine,
    onAddLine,
    onRemoveLine,
    onUndoRemoveLine,
    onCreate,
    isCreating,
    onScrollToPrescription,
}) => {
    const [isAdding, setIsAdding] = useState(false);
    const [removedEntry, setRemovedEntry] = useState(null);
    const removeTimerRef = useRef(null);

    const ready = lines.filter((line) => line.included && line.status === LINE_STATUS.MATCHED);
    const pending = lines.filter((line) => line.status !== LINE_STATUS.MATCHED);
    const scheduleH = ready.filter((line) => line.isScheduleH);

    useEffect(() => () => { if (removeTimerRef.current) clearTimeout(removeTimerRef.current); }, []);

    const handleRemoveLine = (lineId) => {
        const idx = lines.findIndex((l) => l.id === lineId);
        const line = lines[idx];
        onRemoveLine(lineId);
        if (removeTimerRef.current) clearTimeout(removeTimerRef.current);
        setRemovedEntry({ line, idx });
        removeTimerRef.current = setTimeout(() => {
            setRemovedEntry(null);
            removeTimerRef.current = null;
        }, 5000);
    };

    const handleUndoRemove = () => {
        if (!removedEntry) return;
        clearTimeout(removeTimerRef.current);
        removeTimerRef.current = null;
        onUndoRemoveLine(removedEntry.line, removedEntry.idx);
        setRemovedEntry(null);
    };

    const jumpToNextPending = () => {
        if (!pending.length) return;
        const currentIdx = pending.findIndex((l) => l.id === activeLineId);
        const nextId = pending[(currentIdx + 1) % pending.length].id;
        const el = document.querySelector(`[data-line-id="${nextId}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        onSelectLine(nextId);
    };

    return (
        <div className="flex h-full flex-col">
            {/* Mobile-only shortcut to the prescription image above. */}
            {onScrollToPrescription && (
                <button
                    type="button"
                    onClick={onScrollToPrescription}
                    className="mb-4 flex items-center gap-1.5 self-start text-xs font-semibold text-elm-600 hover:text-elm-700 lg:hidden"
                >
                    <FiArrowUp size={12} />
                    View prescription
                </button>
            )}

            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-cod-gray-400">
                    Medicines found
                </h2>
                <p className="text-xs tabular-nums text-cod-gray-400">
                    {lines.length === 0
                        ? "No medicines"
                        : ready.length === lines.length
                            ? `All ${lines.length} ready`
                            : `${ready.length} of ${lines.length} ready`}
                </p>
            </div>

            {/* Jump bar — only shown when there are lines needing a decision. */}
            {pending.length > 0 && (
                <div className="mt-2.5 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    <span className="text-xs font-semibold text-amber-700">
                        {pending.length === 1 ? "1 medicine needs" : `${pending.length} medicines need`} a decision
                    </span>
                    <button
                        type="button"
                        onClick={jumpToNextPending}
                        className="text-xs font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-900"
                    >
                        Next ↓
                    </button>
                </div>
            )}

            {lines.length === 0 ? (
                <div className="mt-3 flex flex-col items-center rounded-xl border-2 border-dashed border-cod-gray-200 py-12 text-center">
                    <p className="text-sm font-medium text-cod-gray-500">No medicines</p>
                    <p className="mt-1 text-xs text-cod-gray-400">Add one below to include it in the quotation.</p>
                </div>
            ) : (
                <ul className="mt-3 flex flex-col gap-2.5">
                    {lines.map((line) => (
                        <MedicineLine
                            key={line.id}
                            line={line}
                            isActive={activeLineId === line.id}
                            onSelect={onSelectLine}
                            onChange={onChangeLine}
                            onResolve={(pick) => onResolveLine(line.id, pick)}
                            onRemove={handleRemoveLine}
                        />
                    ))}
                </ul>
            )}

            {/* For medicines on the prescription that OCR didn't pick up. */}
            <div className="mt-2.5 flex-1">
                {isAdding ? (
                    <div className="rounded-xl border border-cod-gray-100 bg-white-50 p-3.5">
                        <p className="text-[13px] font-semibold text-cod-gray-900">Add a medicine</p>
                        <p className="mt-0.5 text-xs text-cod-gray-400">
                            It starts on one a day for 30 days — set the routine in the quotation.
                        </p>
                        <MedicinePicker
                            onPick={(pick) => {
                                onAddLine(pick);
                                setIsAdding(false);
                            }}
                            onCancel={() => setIsAdding(false)}
                        />
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setIsAdding(true)}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-cod-gray-200 py-3.5 text-sm font-semibold text-cod-gray-500 transition-colors hover:border-elm-400 hover:bg-elm-50 hover:text-elm-700"
                    >
                        <FiPlus size={15} />
                        Add a medicine
                    </button>
                )}
            </div>

            <div className="sticky bottom-0 mt-4 border-t border-cod-gray-100 bg-cod-gray-50 pb-1 pt-4">
                {scheduleH.length > 0 && (
                    <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        {scheduleH.length === 1 ? "One Schedule H medicine is" : `${scheduleH.length} Schedule H medicines are`} in
                        this quotation. Check the prescription is signed and dated before you send it.
                    </p>
                )}

                {/* Undo strip — shown for 5 s after a line is removed. */}
                {removedEntry && (
                    <div className="mb-3 flex items-center justify-between rounded-lg bg-cod-gray-900 px-4 py-2.5">
                        <span className="text-sm text-white-50">
                            &ldquo;{removedEntry.line.name || removedEntry.line.rawName}&rdquo; removed
                        </span>
                        <button
                            type="button"
                            onClick={handleUndoRemove}
                            className="ml-4 text-sm font-semibold text-elm-300 hover:text-elm-200"
                        >
                            Undo
                        </button>
                    </div>
                )}

                <button
                    type="button"
                    onClick={onCreate}
                    disabled={!ready.length || isCreating}
                    className="w-full rounded-xl bg-elm-600 py-3.5 text-[15px] font-semibold text-white-50 transition-colors hover:bg-elm-700 disabled:cursor-not-allowed disabled:bg-cod-gray-200"
                >
                    {isCreating
                        ? "Creating the quotation…"
                        : `Create quotation${ready.length ? ` · ${ready.length} ${ready.length === 1 ? "medicine" : "medicines"}` : ""}`}
                </button>

                {!ready.length && (
                    <p className="mt-2 text-center text-xs text-cod-gray-400">
                        Match at least one medicine to create the quotation.
                    </p>
                )}
            </div>
        </div>
    );
};

export default MedicinesStep;
